import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { showErrorToast } from '../notifications/toast';
import { MyAppsIcon } from './MyAppsIcon';
import {
  canReorderPinnedCatalog,
  compareCatalogEntries,
  matchesCatalogFilter,
  MY_APPS_CATEGORY_FILTERS,
  type MyAppsCatalogFilter
} from './myAppsCatalogFilters';
import { launchMyAppsEntry, searchMyApps } from './myAppsSearch';
import type { MyAppsRegistryEntry, MyAppsSearchResult } from './myAppsTypes';
import { useMyAppsEntries } from './useMyAppsEntries';
import { useMyAppsStore } from './useMyAppsStore';
import './myApps.css';

// DevExtreme diffs options by identity, so keep filter props out of the render body.
const CATEGORY_FILTER_OPTIONS = [...MY_APPS_CATEGORY_FILTERS];
const FILTER_INPUT_ATTR = { 'aria-label': 'Filter apps' };

export interface MyAppsBrowsePageProps {
  title?: string;
  registry?: readonly MyAppsRegistryEntry[];
  /** Shown above the filters; sites word this for their own audience. */
  subtitle?: string;
}

export function MyAppsBrowsePage({ title = 'My Apps', registry, subtitle }: MyAppsBrowsePageProps) {
  const navigate = useNavigate();
  const { status, error, entries, pinnedToolIds, reload } = useMyAppsEntries(registry);
  const saving = useMyAppsStore((s) => s.saving);
  const togglePin = useMyAppsStore((s) => s.togglePin);
  const movePin = useMyAppsStore((s) => s.movePin);
  const recordRecent = useMyAppsStore((s) => s.recordRecent);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<MyAppsCatalogFilter>('All');

  const pinnedSet = useMemo(
    () => new Set(pinnedToolIds.map((id) => id.toLowerCase())),
    [pinnedToolIds]
  );

  const filtered = useMemo(() => {
    const searched = searchMyApps(entries, query);
    const next = searched.filter((entry) => matchesCatalogFilter(entry, category, pinnedSet));
    if (query.trim()) return next;
    return [...next].sort((left, right) =>
      compareCatalogEntries(left, right, pinnedSet, pinnedToolIds)
    );
  }, [category, entries, pinnedSet, pinnedToolIds, query]);

  const allowPinReorder = canReorderPinnedCatalog(category, query);

  async function onTogglePin(entry: MyAppsSearchResult, pinned: boolean) {
    try {
      await togglePin(entry.id, pinned);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not update pinned apps.');
    }
  }

  async function onMovePin(entry: MyAppsSearchResult, direction: -1 | 1) {
    try {
      await movePin(entry.id, direction);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not reorder pinned apps.');
    }
  }

  return (
    <div className="ops-page my-apps-store">
      <div className="dashboard-header my-apps-store__header">
        <div>
          <h2 className="dashboard-title">{title}</h2>
          <p className="dashboard-subtitle">
            {subtitle ??
              'Browse every app your role can use, including custom links your administrators added. Pin shortcuts for the sidebar, dashboard, and Ctrl+K launcher.'}
          </p>
        </div>
        <div className="my-apps-store__filters">
          <TextBox
            stylingMode="outlined"
            value={query}
            placeholder="Search apps..."
            mode="search"
            showClearButton
            width={220}
            onValueChanged={(e) => setQuery(String(e.value ?? ''))}
          />
          <SelectBox
            stylingMode="outlined"
            dataSource={CATEGORY_FILTER_OPTIONS}
            value={category}
            width={150}
            inputAttr={FILTER_INPUT_ATTR}
            onValueChanged={(e) => setCategory(String(e.value) as MyAppsCatalogFilter)}
          />
        </div>
      </div>

      {status === 'loading' || status === 'idle' ? (
        <div className="my-apps-store__empty">
          <LoadIndicator visible />
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="my-apps-store__empty">
          <p>{error ?? 'Failed to load apps.'}</p>
          <Button text="Retry" type="default" onClick={() => void reload()} />
        </div>
      ) : null}

      {status === 'ready' && filtered.length === 0 ? (
        <div className="my-apps-store__empty">No apps match your filters.</div>
      ) : null}

      {status === 'ready' && filtered.length > 0 ? (
        <div className="my-apps-store__grid" role="list" aria-label="My Apps">
          {filtered.map((item) => {
            const isPinned = pinnedSet.has(item.id.toLowerCase());
            const pinIndex = pinnedToolIds.findIndex(
              (id) => id.toLowerCase() === item.id.toLowerCase()
            );
            const showReorder = allowPinReorder && isPinned && pinIndex >= 0;
            return (
              <article
                key={item.id}
                className={['my-apps-store__card', isPinned ? 'my-apps-store__card--pinned' : '']
                  .filter(Boolean)
                  .join(' ')}
                role="listitem"
              >
                <div className="my-apps-store__card-main">
                  <div className="my-apps-store__card-title">
                    <MyAppsIcon icon={item.icon} />
                    <span>{item.title}</span>
                  </div>
                  <p className="my-apps-store__card-desc">{item.description}</p>
                  <div className="my-apps-store__meta">
                    <span className="my-apps-store__badge">{item.category}</span>
                    <span className="my-apps-store__badge">
                      {item.kind === 'link' ? 'Link' : 'App'}
                    </span>
                    {isPinned ? (
                      <span className="my-apps-store__muted">Pinned shortcut</span>
                    ) : null}
                  </div>
                </div>
                <div className="my-apps-store__card-actions">
                  {showReorder ? (
                    <div className="my-apps-store__reorder">
                      <Button
                        icon="chevronup"
                        stylingMode="text"
                        disabled={saving || pinIndex <= 0}
                        hint={`Move ${item.title} up`}
                        onClick={() => void onMovePin(item, -1)}
                      />
                      <Button
                        icon="chevrondown"
                        stylingMode="text"
                        disabled={saving || pinIndex >= pinnedToolIds.length - 1}
                        hint={`Move ${item.title} down`}
                        onClick={() => void onMovePin(item, 1)}
                      />
                    </div>
                  ) : null}
                  <Button
                    text={isPinned ? 'Unpin' : 'Pin'}
                    stylingMode={isPinned ? 'contained' : 'outlined'}
                    disabled={saving}
                    hint={isPinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
                    onClick={() => void onTogglePin(item, !isPinned)}
                  />
                  <Button
                    text="Open"
                    type="default"
                    stylingMode="contained"
                    hint={`Open ${item.title}`}
                    onClick={() => {
                      if (!launchMyAppsEntry(item, navigate)) {
                        showErrorToast('Could not open this app.');
                        return;
                      }
                      void recordRecent(item.id).catch(() => undefined);
                    }}
                  />
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
